import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Bot, User, Sparkles, Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/config';
import { chatWithLoyaltyAI } from '../services/aiService';

const AIChatSidebar = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { effectiveTenantId, tenantData } = useAuth();
    const [messages, setMessages] = useState([
        { role: 'assistant', content: t('aiChat.welcome', 'Merhaba! Ben Premium CRM teknik destek asistanın. Paneli nasıl kullanacağın veya bir işlemi nasıl yapacağın konusunda sana adım adım rehberlik edebilirim. Ne yapmak istersin?') }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const getContextData = async () => {
        if (!effectiveTenantId) return null;

        try {
            // Fetch everything for a deep analysis
            const { data: customersData } = await supabase.from('customers').select('*').eq('tenant_id', effectiveTenantId);
            const { data: discountsData } = await supabase.from('discounts').select('*').eq('tenant_id', effectiveTenantId);
            const { data: activitiesData } = await supabase.from('activities').select('*').eq('tenant_id', effectiveTenantId).order('timestamp', { ascending: false }).limit(20);

            const allDiscounts = discountsData || [];

            const stats = {
                businessName: tenantData?.name,
                businessSlug: tenantData?.slug,
                industry: tenantData?.industry,
                totalCustomers: (customersData || []).length,
                customers: (customersData || []).map(d => ({
                    status: d.status,
                    points: d.points,
                    interactionCount: d.interaction_count,
                    lastInteractionAt: d.last_interaction_at
                })),
                activeDiscounts: allDiscounts.filter(d => !d.is_reward).map(d => ({ title: d.title, type: d.type, value: d.value, usageCount: d.usage_count })),
                availableRewards: allDiscounts.filter(d => d.is_reward).map(d => ({ title: d.title, points: d.required_points, type: d.reward_type })),
                recentActivities: (activitiesData || []).map(data => ({
                    action: data.action,
                    details: data.details,
                    time: data.timestamp
                })),
                loyaltyTiers: tenantData?.loyaltyTiers || []
            };

            return stats;
        } catch (err) {
            console.error("Error gathering AI context:", err);
            return null;
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMessage = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const contextData = await getContextData();
            const response = await chatWithLoyaltyAI(input, messages, contextData);
            setMessages(prev => [...prev, { role: 'assistant', content: response }]);
        } catch (err) {
            console.error("AI Chat Error:", err);
            setMessages(prev => [...prev, { role: 'assistant', content: t('aiChat.error', 'Üzgünüm, bir hata oluştu. Lütfen tekrar dene.') }]);
        } finally {
            setLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([
            { role: 'assistant', content: t('aiChat.welcome', 'Merhaba! Ben Premium CRM teknik destek asistanın. Paneli nasıl kullanacağın veya bir işlemi nasıl yapacağın konusunda sana adım adım rehberlik edebilirim. Ne yapmak istersin?') }
        ]);
    };

    if (!isOpen) return null;

    return (
        <div className="ai-sidebar-panel" style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px',
            background: 'var(--bg-sidebar)', borderLeft: '1px solid var(--border)',
            boxShadow: '-10px 0 30px rgba(0,0,0,0.1)', zIndex: 2000,
            display: 'flex', flexDirection: 'column',
            animation: 'slideIn 0.3s ease-out'
        }}>
            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                @media (max-width: 600px) {
                    .ai-sidebar-panel { width: 100% !important; border-left: none !important; }
                }

                .chat-messages::-webkit-scrollbar {
                    width: 6px;
                }
                .chat-messages::-webkit-scrollbar-thumb {
                    background: var(--border);
                    border-radius: 10px;
                }
                
                .message {
                    max-width: 85%;
                    padding: 14px 18px;
                    border-radius: 18px;
                    margin-bottom: 20px;
                    font-size: 1rem;
                    line-height: 1.6;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
                }
                .message.user {
                    align-self: flex-end;
                    background: var(--primary);
                    color: var(--text-on-primary);
                    border-bottom-right-radius: 4px;
                    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.2);
                }
                .message.assistant {
                    align-self: flex-start;
                    background: var(--bg-card);
                    color: var(--text-main);
                    border-bottom-left-radius: 4px;
                    border: 1px solid var(--border);
                }

                .message strong {
                    color: var(--primary);
                    font-weight: 700;
                }
                
                [data-theme='dark'] .message.assistant {
                    background: rgba(255, 255, 255, 0.03);
                }
                
                [data-theme='dark'] .message strong {
                    color: #d1b8a5; /* Lighter mocha for readability in dark mode */
                }
            `}</style>

            {/* Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(var(--primary-rgb), 0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        background: 'var(--primary)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.2)'
                    }}>
                        <Sparkles size={20} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Teknik Destek Asistanı</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#10b981' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></div>
                            Çevrimiçi
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={clearChat}
                        style={{ background: 'transparent', color: 'var(--text-muted)', padding: '8px', border: 'none', cursor: 'pointer' }}
                        title="Sohbeti Temizle"
                    >
                        <Trash2 size={18} />
                    </button>
                    <button
                        onClick={onClose}
                        style={{ background: 'transparent', color: 'var(--text-main)', padding: '8px', border: 'none', cursor: 'pointer' }}
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="chat-messages" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                {messages.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.role}`} style={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content.split(/(\*\*.*?\*\*)/).map((part, i) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={i}>{part.slice(2, -2)}</strong>;
                            }
                            return part;
                        })}
                    </div>
                ))}
                {loading && (
                    <div className="message assistant" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Analiz ediliyor...</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border)' }}>
                <form onSubmit={handleSend} style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Örn: Nasıl bildirim gönderebilirim?"
                        style={{
                            flex: 1,
                            padding: '14px 18px',
                            background: 'var(--bg-main)',
                            border: '1px solid var(--border)',
                            borderRadius: '16px',
                            fontSize: '1rem',
                            color: 'var(--text-main)',
                            outline: 'none'
                        }}
                    />
                    <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        style={{
                            width: '44px',
                            height: '44px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '12px',
                            background: loading || !input.trim() ? 'var(--border)' : 'var(--primary)'
                        }}
                    >
                        <Send size={20} color="white" />
                    </button>
                </form>
                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '10px' }}>
                    Yapay zeka işletme verilerini kullanarak tavsiyelerde bulunur.
                </p>
            </div>
        </div>
    );
};

export default AIChatSidebar;
